import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import PanelTemplate from "../../templates/Panel";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import { Loading } from "../../components/Loading";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { hasPlanFeature } from "../../services/auth";
import { PlanFeatureUpgradeModal } from "../../components/PlanFeatureUpgradeModal";
import { PlanFeatureUpgradeButton } from "../../components/PlanFeatureUpgradeButton";
import PanelIntro from "../Integration/tabs/PanelIntro";
import { ApiArt } from "../Integration/tabs/IntroArt";

import "../Integration/tabs/panels.scss";

const ApiIntroIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
    </svg>
);

const CopyIcon = ({ copied }) =>
    copied ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
    );

const ExternalApi = ({ embedded = false }) => {
    const [loadingInitial, setLoadingInitial] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [currentKey, setCurrentKey] = useState(null);
    const [displayValue, setDisplayValue] = useState("");
    const [copied, setCopied] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [hadKeyFromApi, setHadKeyFromApi] = useState(false);
    const [showUpgrade, setShowUpgrade] = useState(false);

    const planAvailable = hasPlanFeature("external_api");

    useEffect(() => {
        if (!planAvailable) return;
        loadKey();
    }, []);

    const loadKey = () => {
        setLoadingInitial(true);
        api.get("/api-keys")
            .then((res) => {
                const response = res.data?.data ?? res.data;
                const keys = Array.isArray(response) ? response : response ? [response] : [];
                setHadKeyFromApi(keys.length > 0);
                if (keys.length > 0) {
                    setCurrentKey(keys[0]);
                    setDisplayValue(keys[0].key ?? keys[0].key_preview ?? "");
                } else {
                    setCurrentKey(null);
                    setDisplayValue("");
                }
            })
            .catch(renderError)
            .finally(() => setLoadingInitial(false));
    };

    const generateKey = () => {
        setGenerating(true);

        const create = () => {
            api.post("/api-keys", { name: "API Key" })
                .then((res) => {
                    const data = res.data?.data ?? res.data;
                    setCurrentKey(data);
                    setDisplayValue(data.key ?? "");
                    toast.success("Chave gerada com sucesso");
                })
                .catch(renderError)
                .finally(() => setGenerating(false));
        };

        if (currentKey?.id) {
            api.delete(`/api-keys/${currentKey.id}`)
                .then(create)
                .catch((err) => {
                    renderError(err);
                    setGenerating(false);
                });
        } else {
            create();
        }
    };

    const handleClickGenerate = () => {
        if (hadKeyFromApi && currentKey?.id) {
            setShowConfirm(true);
        } else {
            generateKey();
        }
    };

    const handleConfirmGenerate = () => {
        setShowConfirm(false);
        generateKey();
    };

    const handleCopy = () => {
        if (!displayValue) return;
        navigator.clipboard.writeText(displayValue).then(() => {
            setCopied(true);
            toast.success("Copiado!");
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const wrap = (children) =>
        embedded ? children : <PanelTemplate id="external-api">{children}</PanelTemplate>;

    if (!planAvailable) {
        return wrap(
            <div className="api-panel">
                <PanelIntro
                    title="O que é a API?"
                    icon={ApiIntroIcon}
                    iconBg="#E6F1FB"
                    iconFg="#185FA5"
                    art={<ApiArt />}
                >
                    <p>
                        A API é a ponte entre a Brinde.me e o sistema que sua
                        empresa já usa (ERP, CRM). Com ela, o seu sistema pode{" "}
                        buscar produtos, consultar orçamentos e pedidos e criar
                        orçamentos de forma automática — sem ninguém precisar abrir o
                        painel ou digitar nada.
                    </p>
                </PanelIntro>
                <div className="panel-card">
                    <h2>Chave de API</h2>
                    <p className="panel-card__sub">
                        Este recurso não está disponível no seu plano atual.
                        Faça upgrade para integrar sistemas externos à Brinde.me.
                    </p>
                    <PlanFeatureUpgradeButton onClick={() => setShowUpgrade(true)} text="Fazer upgrade de plano" />
                </div>
                <PlanFeatureUpgradeModal
                    feature="external_api"
                    show={showUpgrade}
                    close={() => setShowUpgrade(false)}
                />
            </div>
        );
    }

    if (loadingInitial) {
        return wrap(<Loading show={true} />);
    }

    return (
        <>
            <Modal
                title="Atenção"
                show={showConfirm}
                close={() => setShowConfirm(false)}
                footer={
                    <>
                        <Button
                            type="secondary"
                            size="small"
                            text="Cancelar"
                            action={() => setShowConfirm(false)}
                        />
                        <Button
                            type="primary"
                            size="small"
                            text="Gerar nova chave"
                            action={handleConfirmGenerate}
                        />
                    </>
                }
            >
                <div className="section">
                    <div className="api-confirm-warning">
                        <p>Já existe uma chave de API cadastrada. Ao gerar uma nova, a chave atual será <strong>revogada permanentemente</strong>.</p>
                        <p><strong>Atualize sua aplicação</strong> com a nova chave para evitar interrupções no serviço.</p>
                    </div>
                </div>
            </Modal>

            {wrap(
                <div className="api-panel">
                    <PanelIntro
                        title="O que é a API?"
                        icon={ApiIntroIcon}
                        iconBg="#E6F1FB"
                        iconFg="#185FA5"
                        art={<ApiArt />}
                    >
                        <p>
                            A API é a ponte entre a Brinde.me e o sistema que sua
                            empresa já usa (ERP, CRM). Com ela, o seu sistema pode{" "}
                            buscar produtos, consultar orçamentos e pedidos e criar
                            orçamentos de forma automática — sem ninguém precisar abrir o
                            painel ou digitar nada.
                        </p>
                    </PanelIntro>

                    <div className="panel-card">
                        <h2>Chave de API</h2>
                        <p className="panel-card__sub">
                            Gere uma chave para integrar sistemas externos. Ela deve ser enviada no
                            header <code className="inline-code">X-Api-Key</code> de cada requisição.
                        </p>

                        <div className="api-field">
                            <div className="keybox">
                                <span
                                    className={`keybox__value${displayValue ? "" : " empty"}`}
                                    onClick={handleCopy}
                                    title={displayValue ? "Clique para copiar" : ""}
                                >
                                    {displayValue || "Nenhuma chave gerada"}
                                </span>
                                <button
                                    type="button"
                                    className={`keybox__copy${copied ? " copied" : ""}`}
                                    onClick={handleCopy}
                                    disabled={!displayValue}
                                    title="Copiar"
                                >
                                    <CopyIcon copied={copied} />
                                </button>
                            </div>
                            <Button
                                type="primary"
                                size="small"
                                text={currentKey?.id ? "Regerar chave" : "Gerar chave"}
                                action={handleClickGenerate}
                                loading={generating}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ExternalApi;
